import { Loader2, Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/trpc";

// Restated rather than imported: `@agent/*` is a TypeScript-only alias, so the portal cannot pull
// the backend's rule at runtime. MUST stay in sync with MAX_COSIGNERS and the documentType enum
// in packages/agent/src/helpers/selectRentGuaranteeCosigners.ts — the endpoint refuses anything
// this form lets through, so a drift shows up as a rejected save rather than bad data.
const MAX_COSIGNERS = 3;

const DOCUMENT_TYPES = [
  { value: "cc", label: "Cédula de ciudadanía" },
  { value: "ce", label: "Cédula de extranjería" },
  { value: "nit", label: "NIT" },
  { value: "ps", label: "Pasaporte" },
] as const;

type DocumentType = (typeof DOCUMENT_TYPES)[number]["value"];

export type Cosigner = {
  name: string;
  documentType: DocumentType;
  documentNumber: string;
};

/** How a stored cosigner reads in the panel. The document prints its own shorter labels. */
const SHORT_LABEL: Record<DocumentType, string> = {
  cc: "CC",
  ce: "CE",
  nit: "NIT",
  ps: "Pasaporte",
};

const emptyRow = (): Cosigner => ({ name: "", documentType: "cc", documentNumber: "" });

/** A row the endpoint would drop anyway; the form refuses to send one rather than lose it silently. */
const isComplete = (cosigner: Cosigner) =>
  cosigner.name.trim().length > 0 && cosigner.documentNumber.trim().length > 0;

/**
 * The lead's deudores solidarios, recorded by hand during the estudio.
 *
 * Palomma-only and offered only once a fianza request exists — the parent decides both. The whole
 * list is saved in one write, so a row removed here is removed in storage; that is also why an
 * emptied list still saves rather than doing nothing.
 *
 * Collapsed until asked for, like the request form: most leads never have a cosigner, and an open
 * form on every profile would be noise.
 */
const RentGuaranteeCosignersEditor = ({
  leadId,
  merchantId,
  stored,
}: {
  leadId: string;
  merchantId: string;
  stored: Cosigner[];
}) => {
  const utils = trpc.useUtils();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<Cosigner[]>([]);
  const headingId = useId();

  const update = trpc.leads.updateRentGuaranteeCosigners.useMutation();

  const handleStartEditing = () => {
    setDraft(stored.length > 0 ? stored : [emptyRow()]);
    setIsEditing(true);
  };

  const updateRow = (index: number, patch: Partial<Cosigner>) =>
    setDraft((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const handleAddRow = () => setDraft((rows) => [...rows, emptyRow()]);

  const handleRemoveRow = (index: number) => setDraft((rows) => rows.filter((_, i) => i !== index));

  const handleSave = async () => {
    // Blank rows are the editor's own scaffolding — an untouched "Agregar codeudor" row is not a
    // cosigner the analyst meant to record, so it is dropped rather than refused.
    const cosigners = draft.filter(isComplete).map((cosigner) => ({
      name: cosigner.name.trim(),
      documentType: cosigner.documentType,
      documentNumber: cosigner.documentNumber.trim(),
    }));

    try {
      const result = await update.mutateAsync({ leadId, cosigners });
      setIsEditing(false);
      // cosignersUpdatedAt is patched in alongside the list, not dropped: the regenerate notice
      // below this block compares it with when each document was last rendered, and a cache
      // carrying only the new list would keep reporting the issued PDFs as current.
      utils.leads.get.setData({ merchantId, id: leadId }, (prev) =>
        prev
          ? {
              ...prev,
              rentGuarantee: {
                ...(prev.rentGuarantee ?? {}),
                cosigners: result.cosigners,
                cosignersUpdatedAt: result.cosignersUpdatedAt,
              },
            }
          : prev
      );
      toast.success("Deudores solidarios actualizados");
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : "No pudimos guardar los deudores solidarios. Intenta de nuevo."
      );
      console.error("updateRentGuaranteeCosigners failed", error);
    }
  };

  if (!isEditing) {
    return (
      <div className="space-y-2">
        {stored.length === 0 ? (
          <p className="text-sm text-neutral-600">Este lead no tiene deudores solidarios.</p>
        ) : (
          stored.map((cosigner) => (
            <div
              key={`${cosigner.documentType}-${cosigner.documentNumber}`}
              className="flex gap-3 text-sm"
            >
              <Users className="mt-0.5 h-4 w-4 shrink-0 text-neutral-600" />
              <div className="min-w-0 flex-1">
                <div className="text-xs text-neutral-600">Deudor solidario</div>
                <div className="break-words text-neutral-900">
                  {cosigner.name} · {SHORT_LABEL[cosigner.documentType]} {cosigner.documentNumber}
                </div>
              </div>
            </div>
          ))
        )}
        {/* Outline: downloading the document stays the primary action of this section. */}
        <Button variant="outline" onClick={handleStartEditing} className="min-h-11 w-full">
          <Users />
          {stored.length === 0 ? "Agregar deudores solidarios" : "Editar deudores solidarios"}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3" aria-labelledby={headingId}>
      <p id={headingId} className="text-xs text-neutral-600">
        Deudores solidarios (máximo {MAX_COSIGNERS})
      </p>

      {draft.map((cosigner, index) => (
        <div
          key={index}
          className="space-y-2 rounded-md border border-neutral-300 bg-neutral-50 p-3"
        >
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1 space-y-1">
              <Label className="text-xs text-neutral-600">Nombre completo</Label>
              <Input
                value={cosigner.name}
                onChange={(event) => updateRow(index, { name: event.target.value })}
                maxLength={120}
                autoComplete="off"
                disabled={update.isLoading}
              />
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleRemoveRow(index)}
              title="Quitar deudor solidario"
              aria-label={`Quitar deudor solidario ${index + 1}`}
              className="mt-5 min-h-11 min-w-11 shrink-0"
              disabled={update.isLoading}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>

          <div className="flex gap-2">
            <div className="min-w-0 flex-1 space-y-1">
              <Label className="text-xs text-neutral-600">Tipo de documento</Label>
              <Select
                value={cosigner.documentType}
                onValueChange={(value) => updateRow(index, { documentType: value as DocumentType })}
                disabled={update.isLoading}
              >
                <SelectTrigger aria-label={`Tipo de documento del deudor solidario ${index + 1}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <Label className="text-xs text-neutral-600">Número</Label>
              <Input
                value={cosigner.documentNumber}
                onChange={(event) => updateRow(index, { documentNumber: event.target.value })}
                maxLength={40}
                inputMode="numeric"
                autoComplete="off"
                disabled={update.isLoading}
              />
            </div>
          </div>
        </div>
      ))}

      <Button
        variant="outline"
        onClick={handleAddRow}
        disabled={draft.length >= MAX_COSIGNERS || update.isLoading}
        className="min-h-11 w-full"
      >
        <Plus />
        Agregar codeudor
      </Button>

      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={() => setIsEditing(false)}
          disabled={update.isLoading}
          className="min-h-11 flex-1"
        >
          Cancelar
        </Button>
        <Button
          onClick={() => void handleSave()}
          disabled={update.isLoading}
          aria-busy={update.isLoading}
          className="min-h-11 flex-1"
        >
          {update.isLoading ? (
            <Loader2 className="animate-spin motion-reduce:animate-none" />
          ) : null}
          {update.isLoading ? "Guardando…" : "Guardar"}
        </Button>
      </div>
    </div>
  );
};

export default RentGuaranteeCosignersEditor;
